import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../utils/axiosInstance';

const BASE = '/updates';

const api = {
    list: (params) => axiosInstance.get(BASE, { params }).then((r) => r.data),
    get: (id) => axiosInstance.get(`${BASE}/${id}`).then((r) => r.data),
    create: (payload) => axiosInstance.post(BASE, payload).then((r) => r.data),
    patch: (id, payload) => axiosInstance.patch(`${BASE}/${id}`, payload).then((r) => r.data),
    remove: (id) => axiosInstance.delete(`${BASE}/${id}`).then((r) => r.data),
    latest: () => axiosInstance.get(`${BASE}/latest`).then((r) => r.data),
};

const normalizeListResponse = (data) => {
	console.log('Normalizing updates list response:', data);
    if (Array.isArray(data)) return { items: data };
    if (data && Array.isArray(data.items)) return data;
    return { items: [] };
};

export const fetchUpdates = createAsyncThunk(
    'updates/fetchAll',
    async ({ search = '', published } = {}, thunkAPI) => {
        try {
            const params = {};
            if (search) params.q = search;
            if (typeof published === 'boolean') params.published = published;
            const data = await api.list(params);
			const updates = data.data;
            return normalizeListResponse(updates);
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message || 'Failed to load updates');
        }
    }
);

export const fetchUpdateById = createAsyncThunk(
    'updates/fetchOne',
    async (id, thunkAPI) => {
        try {
            const data = await api.get(id);
            return data?.item || data;
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message || 'Failed to load update');
        }
    }
);

export const createUpdate = createAsyncThunk(
    'updates/create',
    async (payload, thunkAPI) => {
        try {
            const data = await api.create(payload);
            return data?.item || data;
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message || 'Failed to create update');
        }
    }
);

export const updateUpdate = createAsyncThunk(
    'updates/update',
    async ({ id, payload }, thunkAPI) => {
        try {
            const data = await api.patch(id, payload);
            return data?.item || data;
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message || 'Failed to update update');
        }
    }
);

export const deleteUpdate = createAsyncThunk(
    'updates/delete',
    async (id, thunkAPI) => {
        try {
            await api.remove(id);
            return id;
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message || 'Failed to delete update');
        }
    }
);

export const toggleUpdatePublished = createAsyncThunk(
    'updates/togglePublished',
    async ({ id, published }, thunkAPI) => {
        try {
            const data = await api.patch(id, { published });
            return data?.item || data;
        } catch (e) {
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message || 'Failed to toggle publish status');
        }
    }
);

export const fetchLatestPublishedUpdate = createAsyncThunk(
    'updates/fetchLatest',
    async (_, thunkAPI) => {
        try {
            const data = await api.latest();
            return data?.item || data || null;
        } catch (e) {
            // Latest is used only for highlighting LIVE; treat 404 as no live update
            if (e?.response?.status === 404) return null;
            return thunkAPI.rejectWithValue(e?.response?.data?.message || e.message || 'Failed to load latest update');
        }
    }
);

const initialState = {
    items: [],
    selected: null,
    latestPublished: null,
    loading: false,
    error: null,
    latestLoading: false,
};

const updatesSlice = createSlice({
    name: 'updates',
    initialState,
    reducers: {
        clearUpdatesError(state) { state.error = null; },
        clearSelectedUpdate(state) { state.selected = null; },
        upsertUpdateLocal(state, action) {
            const upd = action.payload;
            if (!upd || !upd._id) return;
            const idx = state.items.findIndex((u) => u._id === upd._id);
            if (idx >= 0) state.items[idx] = upd;
            else state.items.unshift(upd);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUpdates.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUpdates.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.items || [];
            })
            .addCase(fetchUpdates.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to load updates';
            })

            .addCase(fetchUpdateById.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUpdateById.fulfilled, (state, action) => {
                state.loading = false;
                state.selected = action.payload || null;
            })
            .addCase(fetchUpdateById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to load update';
            })

            .addCase(createUpdate.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createUpdate.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) state.items.unshift(action.payload);
            })
            .addCase(createUpdate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to create update';
            })

            .addCase(updateUpdate.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateUpdate.fulfilled, (state, action) => {
                state.loading = false;
                const upd = action.payload;
                if (!upd || !upd._id) return;
                const idx = state.items.findIndex((u) => u._id === upd._id);
                if (idx >= 0) state.items[idx] = upd;
                if (state.selected?._id === upd._id) state.selected = upd;
            })
            .addCase(updateUpdate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to update update';
            })

            .addCase(deleteUpdate.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteUpdate.fulfilled, (state, action) => {
                state.loading = false;
                const id = action.payload;
                state.items = state.items.filter((u) => u._id !== id);
                if (state.selected?._id === id) state.selected = null;
            })
            .addCase(deleteUpdate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to delete update';
            })

            .addCase(toggleUpdatePublished.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(toggleUpdatePublished.fulfilled, (state, action) => {
                state.loading = false;
                const upd = action.payload;
                if (!upd || !upd._id) return;
                const idx = state.items.findIndex((u) => u._id === upd._id);
                if (idx >= 0) state.items[idx] = upd;
                if (state.selected?._id === upd._id) state.selected = upd;
            })
            .addCase(toggleUpdatePublished.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Failed to toggle publish status';
            })

            .addCase(fetchLatestPublishedUpdate.pending, (state) => {
                state.latestLoading = true;
            })
            .addCase(fetchLatestPublishedUpdate.fulfilled, (state, action) => {
                state.latestLoading = false;
                state.latestPublished = action.payload || null;
            })
            .addCase(fetchLatestPublishedUpdate.rejected, (state, action) => {
                state.latestLoading = false;
                state.error = action.payload || state.error;
            });
    },
});

export const { clearUpdatesError, clearSelectedUpdate, upsertUpdateLocal } = updatesSlice.actions;

export default updatesSlice.reducer;

export const selectUpdatesState = (state) => state.updates;
export const selectUpdates = (state) => state.updates.items;
export const selectLatestPublishedUpdate = (state) => state.updates.latestPublished;
